import React, { useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';

type ApiErrorBody = {
  errorMessage?: string;
};

type Props = {
  branchDomain: string;
};

const StartDayPanel = ({ branchDomain }: Props) => {
  const queryClient = useQueryClient();
  const [startingCash, setStartingCash] = useState('');

  const startDay = useMutation({
    mutationFn: async (amount: number) => {
      await axiosInstance.post(`/api/v1/${branchDomain}/storeDays`, {
        startingCash: amount
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['storeDay', branchDomain]
      });
    },
    onError: (error: unknown) => {
      if (
        error instanceof AxiosError &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        toast.error('Only a cashier can start the day.', toastConfig);
        return;
      }
      const message =
        error instanceof AxiosError
          ? (error.response?.data as ApiErrorBody | undefined)?.errorMessage
          : undefined;
      toast.error(message || 'Could not start the day.', toastConfig);
    }
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = Number(startingCash);
    if (startingCash.trim() === '' || !Number.isFinite(amount) || amount < 0) {
      toast.error('Enter a starting cash amount of zero or more.', toastConfig);
      return;
    }
    startDay.mutate(amount);
  };

  return (
    <Card className="mx-auto" style={{ maxWidth: '28rem' }}>
      <Card.Body className="p-4">
        <h5 className="mb-1">Start the day</h5>
        <p className="text-body-tertiary fs-9 mb-4">
          Enter the cash in the drawer before sales begin.
        </p>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="startingCash">
            <Form.Label>Starting Cash</Form.Label>
            <Form.Control
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={startingCash}
              autoFocus
              onChange={event => setStartingCash(event.target.value)}
            />
          </Form.Group>
          <Button
            type="submit"
            variant="primary"
            className="w-100"
            disabled={startDay.isPending || startingCash.trim() === ''}
          >
            {startDay.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Start Day'
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default StartDayPanel;
